import { call, fork, put, takeEvery, select } from 'redux-saga/effects';

import {
  FETCH_ALERTS,
  fetchAlertsFail,
  fetchAlertsSuccess,
  fetchAlerts,
  FETCH_TYPOLOGIES,
  fetchTypologiesSuccess,
  fetchTypologiesFail,
  FETCH_SOURCES,
  fetchSourcesSuccess,
  fetchSourcesFail,
  FETCH_ALERT_RETRY_HISTORY_LIST,
  fetchAlertRetryHistoryListSuccess,
  fetchAlertRetryHistoryListFail
} from '../../redux/actions/cm/alertList';

import {
  ASSIGN_ALERTS_TO_USER,
  assignAlertsToUserFail,
  assignAlertsToUserSuccess,
  ALERT_ACTION_INFO_MODAL_CLOSE,
  BULK_UPDATE_ALERT_STATUS,
  bulkUpdateAlertStatusSuccess,
  bulkUpdateAlertStatusFail,
  BULK_UPDATE_CASE_STATUS,
  bulkUpdateCaseStatusSuccess,
  bulkUpdateCaseStatusFail,
  APPLY_ALERT_STATUS_BULK_IN_CASE,
  applyAlertStatusBulkInCaseSuccess,
  applyAlertStatusBulkInCaseFail
} from '../../redux/actions/cm/alertAction';

import {
  fetchAlerts as fetchAlertsService,
  assignAlertsToUser,
  bulkUpdateAlertStatus as bulkUpdateAlertStatusService,
  bulkUpdateCaseStatus as bulkUpdateCaseStatusService,
  applyAlertStatusBulkInCase as applyAlertStatusBulkInCaseService,
  fetchAlertsRelayHistoryList
} from '../../services/cm/alerts';
import {
  fetchTypologies as fetchTypologiesService,
  fetchSources
} from '../../services/cm/general';

function* fetchAlertListWorker({ filters, currentPage, pageSize }) {
  try {
    const { alerts, total } = yield call(fetchAlertsService, {
      filters,
      currentPage,
      pageSize
    });

    yield put(fetchAlertsSuccess({ alerts, total }));
  } catch (error) {
    yield put(fetchAlertsFail());
  }
}

export function* watchFetchAlertList() {
  yield takeEvery(
    FETCH_ALERTS,
    function* ({ payload: { filters, currentPage, pageSize } }) {
      yield fork(fetchAlertListWorker, { filters, currentPage, pageSize });
    }
  );
}

function* assignAlertsToUserWorker({ alertIds, user }) {
  try {
    yield call(assignAlertsToUser, {
      alertIds,
      userId: user
    });
    yield put(assignAlertsToUserSuccess());
  } catch (error) {
    yield put(assignAlertsToUserFail(error.message));
  }
}

export function* watchAssignAlertsToUser() {
  yield takeEvery(
    ASSIGN_ALERTS_TO_USER,
    function* ({ payload: { alertIds, user } }) {
      yield fork(assignAlertsToUserWorker, { alertIds, user });
    }
  );
}

function* alertActionInfoModalClose() {
  try {
    const getFilters = (state) => state.cmAlertList.filters;
    const getCurrentPage = (state) => state.cmAlertList.currentPage;
    const getPageSize = (state) => state.cmAlertList.pageSize;

    yield put(
      fetchAlerts({
        filters: yield select(getFilters),
        currentPage: yield select(getCurrentPage),
        pageSize: yield select(getPageSize)
      })
    );
  } catch (error) {
    yield put(assignAlertsToUserFail());
  }
}

export function* watchAlertActionInfoModalClose() {
  yield takeEvery(ALERT_ACTION_INFO_MODAL_CLOSE, function* () {
    yield fork(alertActionInfoModalClose);
  });
}

function* fetchTypologiesWorker() {
  try {
    const typologies = yield call(fetchTypologiesService);

    yield put(fetchTypologiesSuccess(typologies));
  } catch (error) {
    yield put(fetchTypologiesFail());
  }
}

export function* watchFetchTypologies() {
  yield takeEvery(FETCH_TYPOLOGIES, function* () {
    yield fork(fetchTypologiesWorker);
  });
}

function* fetchSourcesWorker() {
  try {
    const sources = yield call(fetchSources);

    yield put(fetchSourcesSuccess(sources));
  } catch (error) {
    yield put(fetchSourcesFail());
  }
}

export function* watchFetchSources() {
  yield takeEvery(FETCH_SOURCES, function* () {
    yield fork(fetchSourcesWorker);
  });
}

function* fetchAlertRetryHistoryListWorker({ filters, currentPage, pageSize }) {
  try {
    const alertsRetryHistoryList = yield call(fetchAlertsRelayHistoryList, {
      filters,
      currentPage,
      pageSize
    });

    yield put(fetchAlertRetryHistoryListSuccess(alertsRetryHistoryList));
  } catch (error) {
    yield put(fetchAlertRetryHistoryListFail());
  }
}

export function* watchFetchAlertRetryHistoryList() {
  yield takeEvery(
    FETCH_ALERT_RETRY_HISTORY_LIST,
    function* ({ payload: { filters, currentPage, pageSize } }) {
      yield fork(fetchAlertRetryHistoryListWorker, {
        filters,
        currentPage,
        pageSize
      });
    }
  );
}

function* bulkUpdateAlertStatusWorker({ alertIds, status, reason }) {
  try {
    const result = yield call(bulkUpdateAlertStatusService, {
      alertIds,
      status,
      reason
    });
    yield put(bulkUpdateAlertStatusSuccess(result));
  } catch (error) {
    yield put(bulkUpdateAlertStatusFail(error.message));
  }
}

export function* watchBulkUpdateAlertStatus() {
  yield takeEvery(
    BULK_UPDATE_ALERT_STATUS,
    function* ({ payload: { alertIds, status, reason } }) {
      yield fork(bulkUpdateAlertStatusWorker, { alertIds, status, reason });
    }
  );
}

// FIN-10515: Accept doChecker for checker review workflow
function* bulkUpdateCaseStatusWorker({ caseIds, status, reason, doChecker }) {
  try {
    const result = yield call(bulkUpdateCaseStatusService, {
      caseIds,
      status,
      reason,
      doChecker
    });
    yield put(bulkUpdateCaseStatusSuccess(result));
  } catch (error) {
    yield put(bulkUpdateCaseStatusFail(error.message));
  }
}

export function* watchBulkUpdateCaseStatus() {
  yield takeEvery(
    BULK_UPDATE_CASE_STATUS,
    function* ({ payload: { caseIds, status, reason, doChecker } }) {
      yield fork(bulkUpdateCaseStatusWorker, {
        caseIds,
        status,
        reason,
        doChecker
      });
    }
  );
}

// FIN-10098: Apply alert status to other alerts in case
function* applyAlertStatusBulkInCaseWorker({
  caseId,
  alertIds,
  targetStatus,
  reason,
  triggeringAlertId
}) {
  try {
    const result = yield call(applyAlertStatusBulkInCaseService, {
      caseId,
      alertIds,
      targetStatus,
      reason,
      triggeringAlertId
    });
    yield put(applyAlertStatusBulkInCaseSuccess(result));
  } catch (error) {
    yield put(applyAlertStatusBulkInCaseFail(error.message));
  }
}

export function* watchApplyAlertStatusBulkInCase() {
  yield takeEvery(
    APPLY_ALERT_STATUS_BULK_IN_CASE,
    function* ({
      payload: { caseId, alertIds, targetStatus, reason, triggeringAlertId }
    }) {
      yield fork(applyAlertStatusBulkInCaseWorker, {
        caseId,
        alertIds,
        targetStatus,
        reason,
        triggeringAlertId
      });
    }
  );
}
